import type { CandidateRequirementsDTO } from '@/entities/questionnaire';
import { QuestionKey } from '@/entities/questionnaire';
import { questionnaireRequirementsToCandidateRequirements } from './questionnaire-config-mappers';
import { buildQuestionnaireState, getShouldShowQuestion } from './questionnaire-state';
import { useQuestionnaireConfigurationQuery } from '../api/use-get-questionnaire-configuration-query';
import { useCallback, useEffect, useState } from 'react';

export type UseQuestionnaireFormParams = {
  jobId?: number | null;
};

export function useQuestionnaireForm({ jobId }: UseQuestionnaireFormParams) {
  const [questionnaireState, setQuestionnaireState] = useState<CandidateRequirementsDTO | null>(null);

  const { data: config, isLoading } = useQuestionnaireConfigurationQuery(jobId ?? undefined);

  useEffect(() => {
    if (config?.questionnaire_requirements) {
      const requirements = questionnaireRequirementsToCandidateRequirements(config.questionnaire_requirements);
      setQuestionnaireState(buildQuestionnaireState(requirements));
    }
  }, [config?.questionnaire_requirements]);

  const updateQuestionnaireRequirements = useCallback(
    <K extends QuestionKey>(questionKey: K, jobRequirements: CandidateRequirementsDTO[K]['job_requirements']) => {
      setQuestionnaireState((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          [questionKey]: {
            ...prev[questionKey],
            job_requirements: jobRequirements,
          },
        };
      });
    },
    [],
  );

  const updateQuestionnaireFieldUnit = useCallback(
    <K extends QuestionKey>(questionKey: K, unit: CandidateRequirementsDTO[K]['unit']) => {
      setQuestionnaireState((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          [questionKey]: {
            ...prev[questionKey],
            unit: unit,
          },
        };
      });
    },
    [],
  );

  const updateQuestionnaireRequired = useCallback(<K extends QuestionKey>(questionKey: K, isRequired: boolean) => {
    setQuestionnaireState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [questionKey]: {
          ...prev[questionKey],
          is_required: isRequired,
        },
      };
    });
  }, []);

  const shouldShowQuestion = useCallback(
    (questionKey: QuestionKey) => {
      return getShouldShowQuestion(questionnaireState ?? undefined, questionKey);
    },
    [questionnaireState],
  );

  return {
    questions: questionnaireState,
    questionnaireState,
    updateQuestionnaireRequirements,
    updateQuestionnaireRequired,
    updateQuestionnaireFieldUnit,
    isLoading,
    shouldShowQuestion,
    pendingReview: config?.pending_review === true,
  };
}

export type QuestionnaireRequirement = CandidateRequirementsDTO[QuestionKey];
